import {
    Component,
    computed,
    effect,
    inject,
    OnInit,
    signal,
    viewChild,
    viewChildren
} from "@angular/core";
import { AbstractControl, FormControlOptions, ValidationErrors, Validators, FormBuilder, ValidatorFn } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { OrganizationService } from "./organization.service";
import { emailMaxLengthValidator, notEmptyValidator, ValidationPatterns } from "@bbwt/modules/validation";
import {
    filter,
    tap,
    catchError,
    firstValueFrom,
    map,
    of,
    switchMap
} from "rxjs";
import { IOrganization } from ".";
import { EnabledModule } from "./enabled-module";
import { GoverningBodyService } from "@project/governingbody/governingbody.service";
import { MessageService, SelectItem } from "primeng/api";
import { IGoverningBody } from "@project/governingbody/governingbody";
import { UserHelperService } from "@main/users/user-helper.service";
import {
    DuplicatePatientCheckType,
    createOptions as createDuplicatePatientCheckTypeOptions
} from "@project/governingbody/duplicate-patient-check-type";
import { SystemCodeType } from "@project/governingbody/system-code-type";
import { MiProModules } from "@mipro/classes/mipro-modules";
import { Permissions } from "@main/permissions/permissions";
import { CoreRole } from "@main/roles";
import { FormIOModuleService } from "@main/formio/services/formio-module.service";
import { FormModuleDTO } from "@main/formio/dto/formModuleDTO";
import { FormButtonsConfig } from "@mipro/components";
import { IM1Service } from "@project/services/im1.service";
import { Message } from "@bbwt/classes";
import { DashboardService } from "@main/reporting.v3/dashboard/api/dashboard.service";
import { IModuleDefaultableDashboard } from "@main/reporting.v3/dashboard/dashboard-models";
import { AutoApproveType, createOptions as createAutoApproveTypeOptions } from "@main/companies/auto-approve-type";
import { DefaultSelfRegisterPatientWelcomeSubtitles, DefaultSelfRegisterPatientWelcomeTitle, SelfRegisterLoginMode } from "@main/companies/self-register-login-mode";
import { RadioButton } from "primeng/radiobutton";
import { Dropdown } from "primeng/dropdown";
import * as moment from "moment/moment";
import { FileUpload } from "primeng/fileupload";
import { HttpEventType } from "@angular/common/http";
import { FileDetails } from "@main/file-storage";
import { SrxService } from "@project/services/srx.service";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { conditionalRequiredValidator } from "@bbwt/modules/validation/conditional-required.validator";
import { HomeScreenMode } from "@main/users/home-screen-mode";

export enum SrxCredentialsStatus {
    SrxCredentialsTestSuccess,
    SrxCredentialsTestFailed,
    SrxCredentialsSaveSuccess,
    SrxCredentialsSaveFailed,

    SrxOAuth2TestSuccess,
    SrxOAuth2TestFailed,
    SrxOAuth2SaveSuccess,
    SrxOAuth2SaveFailed
}

export enum ModuleType {
    Default = 1,
    Formio = 2
}

export function urlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) return null;

    try {
      new URL(value);
      return null;
    } catch {
      return { invalidUrl: true };
    }
  };
}

@Component({
    selector: "company-details",
    templateUrl: "./company-details.component.html",
    styleUrls: ["./company-details.component.scss"]
})
export class CompanyDetailsComponent implements OnInit {
    protected readonly SrxCredentialsStatus = SrxCredentialsStatus;
    protected readonly SelfRegisterLoginMode = SelfRegisterLoginMode;
    protected readonly HomeScreenMode = HomeScreenMode;

    protected readonly governingBodyService = inject(GoverningBodyService);
    protected readonly organizationService = inject(OrganizationService);
    protected readonly userHelperService = inject(UserHelperService);
    protected readonly formIOModuleService = inject(FormIOModuleService);
    protected readonly router = inject(Router);
    protected readonly route = inject(ActivatedRoute);
    protected readonly fb = inject(FormBuilder);
    protected readonly dashboardService = inject(DashboardService);
    protected readonly messageService = inject(MessageService);
    protected readonly im1Service = inject(IM1Service);
    protected readonly srxService = inject(SrxService);

    readonly maxLogoFileSize: number = 1048576;

    isCreate: boolean = true;
    govBodyOptions: Array<SelectItem> = [];

    allFormioModules: FormModuleDTO[] = [];
    allGovBodies: IGoverningBody[] = [];

    buttonsConfig = <FormButtonsConfig>{
        saveLabel: "Yes",
        saveFunc: () => {},
        justifyContent: "end"
    };

    protected readonly testingSrxCredentials = signal(false);
    protected readonly srxEmailInitialValue = signal<string>(null);
    protected readonly srxPasswordIsSaved = signal(false);
    protected readonly savingSrxCredentials = signal(false);
    protected readonly srxCredentialsStatusResponse = signal<SrxCredentialsStatus>(null);

    srxCredentialsForm = this.fb.group({
        signatureRxApiEmail: [<string>null, Validators.required],
        signatureRxApiPassword: [<string>null]
    });

    protected readonly testingOAuth2Credentials = signal(false);
    protected readonly oAuth2ClientIdInitialValue = signal<string>(null);
    protected readonly savingOAuth2Credentials = signal(false);
    protected readonly oAuth2ClientSecretIsSaved = signal(false);
    protected readonly oAuth2CredentialsStatusResponse = signal<SrxCredentialsStatus>(null);

    srxOAuth2CredentialsForm = this.fb.group({
        signatureRxOAuth2ClientId: [<string>null, Validators.required],
        signatureRxOAuth2ClientSecret: [<string>null]
    });

    protected readonly srxWebhookSecretIsSaved = signal(false);

    organizationForm = this.fb.group({
        id: [<string>null],
        governingBodyId: [<string>null, Validators.required],
        governingBodyName: [<string>null],
        sportTypeName: [<string>null],
        name: [
            <string>null,
            <FormControlOptions>{
                validators: [notEmptyValidator(), Validators.pattern("^[^/]+$")],
                asyncValidators: this.checkDuplName(),
                updateOn: "change"
            }
        ],
        externalId: [
            <string>null,
            <FormControlOptions>{ validators: [Validators.pattern("^[a-zA-Z0-9-]*$")], asyncValidators: this.checkDuplicateExternalId(), updateOn: "change"}
        ],
        addressId: [<string>null],
        address: this.fb.group({
            id: [<string>null],
            address1: [<string>null, <FormControlOptions>{validators: notEmptyValidator()}],
            address2: [<string>null],
            address3: [<string>null],
            address4: [<string>null],
            postCode: [<string>null, <FormControlOptions>{validators: notEmptyValidator()}]
        }),
        phoneNumber: [<string>null, [Validators.required, Validators.pattern(ValidationPatterns.phone)]],
        description: [<string>null],
        mandatoryTwoFactorAuth: [true],
        enabledModules: this.fb.array<EnabledModule>([]),
        diaryModuleVisibleLHSAndCard: [true],
        selfRegisterPatientURLEnabled: [false],
        selfRegisterPatientUrlLoginMode: [SelfRegisterLoginMode.MiProAndNhs],
        selfRegisterPatientWelcomeTitle: [DefaultSelfRegisterPatientWelcomeTitle],
        selfRegisterPatientWelcomeSubtitle: [null],
        showMiProBranding: [true],
        logoImageId: [null],
        logoImageUrl: [null],
        externalPatientIdSavingEnabled: [false],
        externalSiteRedirectBannerEnabled: [false],
        externalSiteURL: [null, [urlValidator()]],
        externalSiteRedirectBannerBody: [<string>null],
        overrideDuplicatePatientCheckType: [false],
        duplicatePatientCheckType: [DuplicatePatientCheckType.None, Validators.required],
        globalURL: [<string>null],
        password: [<string>null],
        overrideSystemCodeType: [false],
        systemCodeType: [SystemCodeType.OSIICS],
        overrideMonthsUntilEpisodeInactive: [false],
        monthsUntilEpisodeInactive: [2, Validators.required],
        preferredPharmacy: this.fb.group({
            name: [<string>null, Validators.maxLength(120)],
            email: [<string>null, [Validators.pattern(ValidationPatterns.email), emailMaxLengthValidator]],
            addressLine1: [<string>null, Validators.maxLength(150)],
            addressLine2: [<string>null, Validators.maxLength(150)],
            addressLine3: [<string>null, Validators.maxLength(150)],
            addressLine4: [<string>null, Validators.maxLength(150)],
            postCode: [<string>null, Validators.maxLength(15)],
            phoneNumber: [<string>null, Validators.pattern(ValidationPatterns.phone)],
            notes: [<string>null]
        }),
        signatureRxEnabled: [false],
        srxWebhookSecret: [<string>null, Validators.pattern(ValidationPatterns.srxWebhookSecret)],
        homeClinicianDashboardId: [<string>null],
        homePatientDashboardId: [<string>null],
        clinicianDefaultHomeScreenMode: [HomeScreenMode.Cards],
        patientDefaultHomeScreenMode: [HomeScreenMode.Cards],
        homeSelectedPatientDashboardId: [<string>null],
        overridePassportDataSharing: [false],
        passportDataSharing: [false],
        overrideAutoApproveSelfRegisteredPatients: [false],
        autoApproveSelfRegisteredPatients: [false],
        overrideMandatoryIdentityVerification: [false],
        mandatoryIdentityVerification: [false],
        cqcRegistrationNumber: [<string>null, Validators.maxLength(120)],
        researchOptOut: [false],
        enablePatientPsdConfirmation: [<boolean>null],
        overrideEnablePatientPsdConfirmation: [false],
        autoApproveType: [AutoApproveType.NoWalkInCreated, Validators.required],
        sendNHSAccessStatusAlerts: [false],
        nhsAccessStatusAlertUserId: [<string>null],
        appointmentBookingURLEnabled: [false],
        enablePatientDocumentUpload: [false],
        autoApproveDataSharing: [true]
    });

    loadingOrganization = true;
    savingOrganization = false;
    originalMandatoryTwoFactorAuth: boolean = true;
    originalLogoImageId: string;
    originalCQCRegistrationNumber = null;
    showNHSAlertsSettings = false;

    cqcRegistrationNumberDialogVisible = false;


    protected cqcRegistrationButtonsConfig = <FormButtonsConfig>{
        saveLabel: "OK",
        saveFunc: () => (this.cqcRegistrationNumberDialogVisible = false),
        cancelFunc: () => {
            this.cqcRegistrationNumberDialogVisible = false;
            const nhsControl = this.enabledModules.controls.find(c => (<EnabledModule>c.value).moduleName === MiProModules.NHS);
            const module = <EnabledModule>{...nhsControl.value, enabled: false};
            nhsControl.patchValue(module);

            this.onModuleEnabledChange(module);
            this.cqcRegistrationNumber.reset(null);
            this.updateCQCRegNumberValidators(module);
        }
    };

    checked = true;

    _showEnablePatientDocumentUploadField: boolean = false;
    
    _showSelfRegistrationURLField: boolean = false;
    _showAppointmentBookingURLField: boolean = false;
    _showFormioModules: boolean = false;

    showSystemDashboards: boolean = false;
    dashboardOptions: Array<SelectItem> = [];

    protected miproModules = MiProModules;
    protected coreRoles = CoreRole;

    globalURL: string = null;
    isFormsModuleEnabled: boolean = false;
    isPrescriptionsModuleEnabled: boolean = false;

    protected readonly organizationFromName = toSignal(this.organizationForm.get("name").valueChanges);
    protected readonly signatureRxEnabled = toSignal(this.organizationForm.get("signatureRxEnabled").valueChanges);
    protected readonly srxWebhookSecret = toSignal(this.organizationForm.get("srxWebhookSecret").valueChanges);

    selfRegisterPatientURL: string = null;

    appointmentBookingURL: string = null;

    protected readonly baseSrxWebhookUrl = toSignal(
        toObservable(this.organizationFromName).pipe(
            switchMap(name => name ? this.srxService.getWebhookUrl(this.organizationId, name) : of(null)),
            catchError(() => of(null))
        )
    );

    protected readonly organizationWebhookUrl = computed(() =>
        this.organizationFromName()
            ? `https://${window.location.host}/api/srx-webhooks/${this.baseSrxWebhookUrl()}`
            : null
    );

    protected readonly signatureRxWebhookSecretRequired = computed(() => this.signatureRxEnabled() && !this.srxWebhookSecretIsSaved());

    protected preferredPharmacyRequiredFields = false;

    defaultableDashboards: IModuleDefaultableDashboard[] = [];

    originalResearchOptOut: boolean = false;

    brandingLogoImageFormData: FormData;
    brandingLogoImagePreviewSrc: string | null = null;

    private logoImageUploader = viewChild<FileUpload>("logoImageUploader");
    private autoApproveTypeDropdown = viewChild<Dropdown>("autoApproveTypeDropdown")
    private selfRegLoginModeButtons = viewChildren<RadioButton>("selfRegLoginMode");

    get selfRegisterPatientUrlEnabled() {
        return this.organizationForm.controls.selfRegisterPatientURLEnabled;
    }

    get showSelfRegistrationURLField() {
        return this._showSelfRegistrationURLField;
    }

    get cqcRegistrationNumber() {
        return this.organizationForm.controls.cqcRegistrationNumber;
    }

    set showSelfRegistrationURLField(enabled: boolean) {
        this._showSelfRegistrationURLField = enabled;
        if (!enabled) {
            this.organizationForm.patchValue({ selfRegisterPatientURLEnabled: false });
        }
    }

    get showEnablePatientDocumentUploadField() {
        return this._showEnablePatientDocumentUploadField;
    }

    set showEnablePatientDocumentUploadField(enabled: boolean) {
        this._showEnablePatientDocumentUploadField = enabled;
        if (!enabled) {
            this.organizationForm.patchValue({ enablePatientDocumentUpload: false });
        }
    }

    set showFormioModules(enabled: boolean) {
        this._showFormioModules = enabled;
    }

    get appointmentBookingURLEnabled() {
        return this.organizationForm.controls.appointmentBookingURLEnabled;
    }

    get showAppointmentBookingURLField() {
        return this._showAppointmentBookingURLField;
    }

    set showAppointmentBookingURLField(enabled: boolean) {
        this._showAppointmentBookingURLField = enabled;
        if (!enabled) {
            this.organizationForm.patchValue({ appointmentBookingURLEnabled: false });
        }
    }

    duplicatePatientCheckTypeOptions = createDuplicatePatientCheckTypeOptions();
    autoApproveOptions = createAutoApproveTypeOptions();

    usersInOrganization: Array<SelectItem> = [];

    private _parentDuplicatePatientCheckType: DuplicatePatientCheckType = DuplicatePatientCheckType.None;
    private _parentSystemCodeType: SystemCodeType = SystemCodeType.OSIICS;
    private _parentMonthsUntilEpisodeInactive: number;
    private _parentPassportDataSharing: boolean;
    private _parentAutoApproveSelfRegisteredPatients: boolean;
    private _parentEnablePatientPsdConfirmation: boolean;
    protected parentMandatoryTwoFactorAuth: boolean;
    protected parentCanOrganisationsOverrideSystemCodeType: boolean = true;

    private selfRegisterPatientLoginModePreviousValue: SelfRegisterLoginMode = null;
    private selfRegisterPatientEditedWelcomeTitles: string[] = [];
    private selfRegisterPatientEditedWelcomeSubtitles: string[] = [];

    get overrideSystemCodeType() {
        return this.organizationForm.controls.overrideSystemCodeType.value;
    }

    get overrideDuplicatePatientCheckType() {
        return this.organizationForm.controls.overrideDuplicatePatientCheckType.value;
    }

    set overrideDuplicatePatientCheckType(value: boolean) {
        this.organizationForm.controls.overrideDuplicatePatientCheckType.setValue(value);
    }

    get organizationId() {
        return this.organizationForm.get("id")?.value;
    }

    get overrideMonthsUntilEpisodeInactive() {
        return this.organizationForm.controls.overrideMonthsUntilEpisodeInactive.value;
    }

    set overrideMonthsUntilEpisodeInactive(value: boolean) {
        this.organizationForm.controls.overrideMonthsUntilEpisodeInactive.setValue(value);
    }

    get overridePassportDataSharing() {
        return this.organizationForm.controls.overridePassportDataSharing.value;
    }

    set overridePassportDataSharing(value: boolean) {
        this.organizationForm.controls.overridePassportDataSharing.setValue(value);
    }

    get overrideAutoApproveSelfRegisteredPatients() {
        return this.organizationForm.controls.overrideAutoApproveSelfRegisteredPatients.value;
    }

    set overrideAutoApproveSelfRegisteredPatients(value: boolean) {
        this.organizationForm.controls.overrideAutoApproveSelfRegisteredPatients.setValue(value);
    }

    get overrideEnablePatientPsdConfirmation() {
        return this.organizationForm.controls.overrideEnablePatientPsdConfirmation.value;
    }

    set overrideEnablePatientPsdConfirmation(value: boolean) {
        this.organizationForm.controls.overrideEnablePatientPsdConfirmation.setValue(value);
    }

    get overrideMandatoryIdentityVerification() {
        return this.organizationForm.controls.overrideMandatoryIdentityVerification.value;
    }

    set overrideMandatoryIdentityVerification(value: boolean) {
        this.organizationForm.controls.overrideMandatoryIdentityVerification.setValue(value);
    }

    get systemCodeType() {
        return this.organizationForm.controls.systemCodeType.value;
    }

    get logoImageUrl() {
        return this.organizationForm.controls.logoImageUrl.value;
    }

    get logoImageId() {
        return this.organizationForm.controls.logoImageId.value;
    }

    protected get preferredPharmacy() {
        return this.organizationForm.controls.preferredPharmacy;
    }

    get enableMandatoryTwoFactorAuthCheckbox() {
        return !this.parentMandatoryTwoFactorAuth && !this.cqcRegistrationNumber?.value;
    }

    get isNhsModuleEnabled(): boolean {
        return this.enabledModules.controls.find(c => c.value.moduleName === MiProModules.NHS)?.value?.enabled ?? false;
    }

    changeOverrideDuplicatePatientCheckType(override: boolean) {
        if (!override) {
            this.organizationForm.patchValue({duplicatePatientCheckType: this._parentDuplicatePatientCheckType});
        }
    }

    changeOverridePassportDataSharing(override: boolean) {
        if (!override) {
            this.organizationForm.patchValue({passportDataSharing: this._parentPassportDataSharing});
        }
    }

    changeOverrideAutoApproveSelfRegisteredPatients(override: boolean) {
        if (!override) {
            this.organizationForm.patchValue({autoApproveSelfRegisteredPatients: this._parentAutoApproveSelfRegisteredPatients});
        }
    }

    changeOverrideMandatoryIdentityVerification(override: boolean) {
        if (!override) {
            this.organizationForm.patchValue({mandatoryIdentityVerification: this.parentMandatoryTwoFactorAuth});
        }
    }

    changeOverrideSystemCodeType(override: boolean) {
        this.organizationForm.patchValue({overrideSystemCodeType: override});
        if (!override) {
            this.organizationForm.patchValue({systemCodeType: this._parentSystemCodeType});
        }
    }

    changeSystemCodeType(systemCode: SystemCodeType) {
        this.organizationForm.patchValue({systemCodeType: systemCode});
    }

    changeOverrideMonthsUntilEpisodeInactive(override: boolean) {
        this.organizationForm.patchValue({overrideMonthsUntilEpisodeInactive: override});
        if (!override) {
            this.organizationForm.patchValue({monthsUntilEpisodeInactive: this._parentMonthsUntilEpisodeInactive});
        }
    }

    constructor() {
        this.subscribeFormEvents();

        effect(() => {
            const webhookSecretControl = this.organizationForm.get("srxWebhookSecret");

            if (this.signatureRxEnabled()) {
                webhookSecretControl?.setValidators([
                    Validators.pattern(ValidationPatterns.srxWebhookSecret),
                    conditionalRequiredValidator(this.signatureRxWebhookSecretRequired())
                ]);
            } else {
                webhookSecretControl.clearValidators();
            }
            webhookSecretControl.updateValueAndValidity();
        })

        effect(() => {
            const passwordControl = this.srxCredentialsForm.get("signatureRxApiPassword");

            passwordControl?.setValidators([conditionalRequiredValidator(!this.srxPasswordIsSaved())]);
            passwordControl?.updateValueAndValidity();
        });

        effect(() => {
            const clientSecretControl = this.srxOAuth2CredentialsForm.get("signatureRxOAuth2ClientSecret");

            clientSecretControl?.setValidators([conditionalRequiredValidator(!this.oAuth2ClientSecretIsSaved())]);
            clientSecretControl?.updateValueAndValidity();
        });
    }

    async ngOnInit() {
        const params = await firstValueFrom(this.route.params);
        const id = params["id"];
        let modules: EnabledModule[] = [];
        this.allGovBodies = await this.governingBodyService.getAllGovBody();
        let isFunctionalGutOrg = false;
        this.allFormioModules = await this.formIOModuleService.getAll();

        if (!this.overrideDuplicatePatientCheckType) {
            this.organizationForm.controls.duplicatePatientCheckType.disable({emitEvent: false});
        } else {
            this.organizationForm.controls.duplicatePatientCheckType.enable({emitEvent: false});
        }

        if (!!id && id != 0) {
            this.isCreate = false;
            const data = await this.organizationService.get(id);
            const currentGovBody = this.allGovBodies.find(x => x.name === data.governingBodyName);
            this.globalURL = `${window.location.protocol}//${window.location.host}/${data.globalURL}`;

            isFunctionalGutOrg = data.name == OrganizationService.FunctionalGutOrgName;
            this.originalMandatoryTwoFactorAuth = data.mandatoryTwoFactorAuth;
            this.originalLogoImageId = data.logoImageId;

            this._parentDuplicatePatientCheckType = currentGovBody.duplicatePatientCheckType;
            this._parentSystemCodeType = currentGovBody.systemCodeType;
            this.parentCanOrganisationsOverrideSystemCodeType = currentGovBody.canOrganisationsOverrideSystemCodeType;
            this._parentMonthsUntilEpisodeInactive = currentGovBody.monthsUntilEpisodeInactive;
            this._parentPassportDataSharing = currentGovBody.passportDataSharing;
            this._parentAutoApproveSelfRegisteredPatients = currentGovBody.autoApproveSelfRegisteredPatients;
            this._parentEnablePatientPsdConfirmation = currentGovBody.enablePatientPsdConfirmation;
            this.parentMandatoryTwoFactorAuth = currentGovBody.mandatoryTwoFactorAuth;

            this.organizationForm.patchValue(data);

            const selfRegInfo = await this.organizationService.getSelfRegistrationInfo(data.id);
            this.organizationForm.patchValue({ logoImageUrl: selfRegInfo?.logoImage?.url });

            this.selfRegisterPatientLoginModePreviousValue = this.organizationForm.get("selfRegisterPatientUrlLoginMode").value;
            const previousTitle = this.organizationForm.get("selfRegisterPatientWelcomeTitle").value;
            this.selfRegisterPatientEditedWelcomeTitles[this.selfRegisterPatientLoginModePreviousValue] = previousTitle ?? DefaultSelfRegisterPatientWelcomeTitle;
            const previousSubtitle = this.organizationForm.get("selfRegisterPatientWelcomeSubtitle").value;
            this.selfRegisterPatientEditedWelcomeSubtitles[this.selfRegisterPatientLoginModePreviousValue] = previousSubtitle ?? DefaultSelfRegisterPatientWelcomeSubtitles[this.selfRegisterPatientLoginModePreviousValue];

            if (data.nhsAccessStatusAlertUserId) {
                this.organizationForm.patchValue({
                    nhsAccessStatusAlertUserId: data.nhsAccessStatusAlertUserId,
                    sendNHSAccessStatusAlerts: true
                });
            }
            const isNhsEnabled = data.enabledModules?.some(m => m.moduleName === MiProModules.NHS && m.enabled);

            if (isNhsEnabled) {
                await this.loadUsersInOrganization(data.id);
            }

            modules = data.enabledModules;

            this.srxWebhookSecretIsSaved.set(data.signatureRxWebhookSecretSaved)

            this.srxCredentialsForm.patchValue(data);
            this.srxPasswordIsSaved.set(data.signatureRxApiPasswordSaved);
            this.srxEmailInitialValue.set(data.signatureRxApiEmail);

            this.srxOAuth2CredentialsForm.patchValue(data);
            this.oAuth2ClientSecretIsSaved.set(data.signatureRxOAuth2ClientSecretSaved);
            this.oAuth2ClientIdInitialValue.set(data.signatureRxOAuth2ClientId);

            let defModules = modules.filter(m => m.type === ModuleType.Default)
            let restModules = modules.filter(m => m.type !== ModuleType.Default)

            // Making default formio module be the last so all formio modules are beneath it
            const fm = defModules.find(m => m.moduleName === MiProModules.Forms);
            if (!!fm) {
                defModules = [...defModules.filter(m => m !== fm), fm];
            }

            let modulesNotInOrg = this.allFormioModules
                .filter(formioModule => modules.every(existingModule => formioModule.name != existingModule.moduleName))
                .map(
                    formioModule =>
                        <EnabledModule>{
                            moduleName: formioModule.name,
                            enabled: false,
                            type: ModuleType.Formio
                        }
                );

            restModules = [...restModules, ...modulesNotInOrg]
                .sort((a, b) => a.moduleName.localeCompare(b.moduleName));

            modules = [...defModules, ...restModules];
            modules = modules.filter(x => x.type != ModuleType.Formio
                || this.allFormioModules.some(module => x.moduleName == module.name));

            this.defaultableDashboards = await this.dashboardService.getAllModuleDefaultableDashboards(id);
        } else {
            this.govBodyOptions = this.allGovBodies.map(item => <SelectItem>{label: item.name, value: item.id});
            this.govBodyOptions = this.allGovBodies
                .filter(
                    gb =>
                        gb.id == this.userHelperService.hashedGoverningBodyId ||
                        this.userHelperService.isMiProAdmin ||
                        this.userHelperService.isSuperAdmin
                )
                .map(item => <SelectItem>{label: item.name, value: item.id});

            let moduleNames = await this.organizationService.getMiproModules();

            // Making default formio module be the last so all formio modules are beneath it
            const fi = moduleNames.indexOf(MiProModules.Forms);
            if (fi >= 0) {
                moduleNames.splice(fi, 1);
                moduleNames.push(MiProModules.Forms);
            }

            modules = moduleNames.map(name => <EnabledModule>{moduleName: name, enabled: false});

            const formIoModules = this.allFormioModules.map(
                formioModule =>
                    <EnabledModule>{
                        moduleName: formioModule.name,
                        enabled: false,
                        type: ModuleType.Formio
                    }
            );

            modules = [...modules, ...formIoModules];
        }

        modules.forEach(m => {
            m.dashboards = this.defaultableDashboards.filter(d => d.modules.includes(m.moduleName));
            const enableModuleControl = this.fb.control(m);

            if (m.moduleName === MiProModules.FunctionalGut) {
                isFunctionalGutOrg ? enableModuleControl.enable() : enableModuleControl.disable();
            } else {
                isFunctionalGutOrg ? enableModuleControl.disable() : enableModuleControl.enable();
            }

            this.enabledModules.push(enableModuleControl);

            if (m.moduleName == MiProModules.NHS) {
                this.originalCQCRegistrationNumber = this.cqcRegistrationNumber.value;
                this.updateCQCRegNumberValidators(m);
            }
        });

        // Make sure all enabled modules are created
        modules.forEach(module => this.onModuleEnabledChange(module, true));

        this.isFormsModuleEnabled = (modules.find(m => m.moduleName === MiProModules.Forms)).enabled;
        this.isPrescriptionsModuleEnabled = (modules.find(m => m.moduleName === MiProModules.Prescriptions)).enabled;

        this.originalResearchOptOut = !!this.organizationForm.get("researchOptOut")?.value;

        this.loadingOrganization = false;

        this.dashboardOptions = (await this.dashboardService.getPage(null)).items.map(d => <SelectItem>{
            label: d.name,
            value: d.id
        });

        this.organizationForm.get("selfRegisterPatientUrlLoginMode")?.valueChanges.subscribe((loginMode) => {
            this.updateSelfRegisterPatientWelcomeMessage(loginMode);
        });

        this.refreshAutoApproveTypeDropdown();
    }

    updateSelfRegisterPatientWelcomeMessage(loginMode: SelfRegisterLoginMode) {
        if(this.selfRegisterPatientLoginModePreviousValue !== null) {
            const previousSubtitle = this.organizationForm.get("selfRegisterPatientWelcomeSubtitle").value;
            this.selfRegisterPatientEditedWelcomeSubtitles[this.selfRegisterPatientLoginModePreviousValue] = previousSubtitle ?? DefaultSelfRegisterPatientWelcomeSubtitles[this.selfRegisterPatientLoginModePreviousValue];

            const previousTitle = this.organizationForm.get("selfRegisterPatientWelcomeTitle").value;
            this.selfRegisterPatientEditedWelcomeTitles[this.selfRegisterPatientLoginModePreviousValue] = previousTitle ?? DefaultSelfRegisterPatientWelcomeTitle;
        }
        this.organizationForm.patchValue({
            selfRegisterPatientWelcomeTitle: this.selfRegisterPatientEditedWelcomeTitles[loginMode] ?? DefaultSelfRegisterPatientWelcomeTitle,
            selfRegisterPatientWelcomeSubtitle: this.selfRegisterPatientEditedWelcomeSubtitles[loginMode] ?? DefaultSelfRegisterPatientWelcomeSubtitles[loginMode]
        });
        this.selfRegisterPatientLoginModePreviousValue = loginMode;
    }

    toggleFormControlState(overrideControlName: string, targetControlName: string) {
        const targetControl = this.organizationForm.get(targetControlName);
        this.organizationForm.get(overrideControlName)?.value
            ? targetControl?.enable({emitEvent: false})
            : targetControl?.disable({emitEvent: false});
    }

    private subscribeFormEvents() {
        this.organizationForm.controls.name.valueChanges.subscribe(_ => {
            this.getSelfRegisterPatientURL();
            this.getAppointmentBookingURL();
        });
        this.preferredPharmacy.valueChanges.subscribe(() => this.updatePreferredPharmacyValidators());

        this.organizationForm.controls.overrideEnablePatientPsdConfirmation.valueChanges.subscribe(override => {
            if (!override) {
                this.organizationForm.controls.enablePatientPsdConfirmation.setValue(this._parentEnablePatientPsdConfirmation);
            }
        });

        const controls = [
            "overrideDuplicatePatientCheckType",
            "overrideMonthsUntilEpisodeInactive",
            "overridePassportDataSharing",
            "overrideAutoApproveSelfRegisteredPatients",
            "overrideEnablePatientPsdConfirmation",
            "overrideMandatoryIdentityVerification"
        ];

        controls.forEach(overrideControlName => {
            this.organizationForm.get(overrideControlName)?.valueChanges.subscribe(() => {
                this.toggleFormControlState(
                    overrideControlName,
                    overrideControlName.replace("override", "").replace(/^\w/, c => c.toLowerCase())
                );
            });
        });
    }

    private checkDuplName() {
        return async (nameCtrl: AbstractControl): Promise<ValidationErrors | null> => {
            const data = await this.organizationService.exists(<IOrganization>{
                id: this.organizationForm.controls.id.value,
                governingBodyId: this.organizationForm.controls.governingBodyId.value,
                name: nameCtrl.value
            });
            return data ? {companyExist: true} : null;
        };
    }

    private checkDuplicateExternalId() {
        return async (externalIdCtrl: AbstractControl): Promise<ValidationErrors | null> => {
            if (!externalIdCtrl.value) {
                return null;
            }

            const data = await this.organizationService.externalIdExists(<IOrganization>{
                id: this.organizationForm.controls.id.value,
                externalId: externalIdCtrl.value
            });

            return data ? { externalIdExist: true } : null;
        };
    }

    get enabledModules() {
        return this.organizationForm.controls.enabledModules;
    }

    get governingBodyName() {
        return this.organizationForm.controls.governingBodyName.value;
    }

    get governingBodyId() {
        return this.organizationForm.get("governingBodyId").value;
    }

    get governingBody() {
        let govBodyId = this.organizationForm.controls.governingBodyId.value;

        return this.allGovBodies.find(x => x.id == govBodyId);
    }

    get sportTypeName() {
        return this.organizationForm.controls.sportTypeName.value;
    }

    get hasManageOrganisationSystemCode() {
        return (
            this.userHelperService.hasPermission(Permissions.ManageOrganisationSystemCode) ||
            this.userHelperService.isSuperAdmin ||
            this.userHelperService.isMiProAdmin
        );
    }

    get ResearchOptHeader(): string {
        return "Research Opt Out";
    }

    get ResearchOptMessage(): string {
        return (this.organizationForm?.get("researchOptOut").value
            ? "You are currently <b>opted out</b> of research. We respect your decision." +
            " If you change your mind and would like to contribute to advancing healthcare and developing better treatments," +
            " you can opt back in at any time by unchecking the checkbox."
            : "You are currently <b>opted in</b> for research. We are committed to protecting your privacy." +
            " Your data helps us improve healthcare and develop better treatments If you prefer not to have your data used for research," +
            " you may <b>opt out</b> at any time by selecting the checkbox.");
    }

    get autoApproveDataSharingMessage(): string {
        return "This setting controls whether patients in this organisation provide passive consent for data sharing.";
    }

    async save() {
        if (this.organizationForm.invalid) {
            this.organizationForm.markAllAsTouched();
            return;
        }

        this.savingOrganization = true;

        if (this.signatureRxEnabled()) {
            const success = await this.saveSrxData();
            if (!success) return;
        }

        const organization = <IOrganization>this.organizationForm.value;
        organization.enabledModules = this.organizationForm.controls.enabledModules.getRawValue();

        if (!this.overrideDuplicatePatientCheckType) {
            organization.duplicatePatientCheckType = null;
        }
        if (!this.overrideSystemCodeType) {
            organization.systemCodeType = null;
        }
        if (!this.overridePassportDataSharing) {
            organization.passportDataSharing = null;
        }
        if (!this.overrideAutoApproveSelfRegisteredPatients) {
            organization.autoApproveSelfRegisteredPatients = null;
        }

        if (!this.overrideEnablePatientPsdConfirmation) {
            organization.enablePatientPsdConfirmation = null;
        }

        if (!this.overrideMandatoryIdentityVerification) {
            organization.mandatoryIdentityVerification = null;
        }

        if (this.isCreate) {
            const result = await this.organizationService.create(organization);
            await this.saveLogoImage(result.id);
        } else {
            const result = await this.organizationService.update(organization.id, organization);
            await this.saveLogoImage(result.id);

            const nhsModule: EnabledModule = this.enabledModules.controls.find(c => c.value.moduleName === MiProModules.NHS)?.value;

            if ((!nhsModule?.enabled && this.originalCQCRegistrationNumber)) {
                await this.im1Service.deleteAllPatientsIM1DataFromOrganisation(organization.id);
                this.messageService.add(Message.Info("The Organisation's NHS Data has been scheduled for deletion", "NHS Data"));
            }

            const currentResearchOptOut = this.organizationForm.get("researchOptOut")?.value;

            const hasResearchOptOutChanged = currentResearchOptOut !== this.originalResearchOptOut;

            if (hasResearchOptOutChanged) {
                this.im1Service.updateIM1DataResearchOptOutByOrganization(organization.id, true, currentResearchOptOut);
            }
        }

        const selectedUserId = this.organizationForm.get("nhsAccessStatusAlertUserId")?.value;

        if (selectedUserId) {
            this.organizationService.updateNHSAccessStatusUser(selectedUserId, this.organizationId);
        }

        this.savingOrganization = false;
        this.router.navigate(["/app/companies"]).then();
    }

    async saveSrxData(): Promise<boolean> {
        if (!this.signatureRxEnabled()) return true;

        if (this.srxWebhookSecret()) {
            await this.srxService.saveWebhookSecret(this.srxWebhookSecret(), this.organizationId);
        }

        const srxEmailHasChanged = this.srxEmailInitialValue() !== this.srxCredentialsForm.value.signatureRxApiEmail;
        if (this.srxCredentialsForm.value.signatureRxApiPassword || srxEmailHasChanged) {
            await this.saveSrxApiCredentials()
            if (this.srxCredentialsStatusResponse() === SrxCredentialsStatus.SrxCredentialsSaveFailed) {
                this.savingOrganization = false;
                return false;
            }
        }
        const oAuth2ClientIdHasChanged = this.oAuth2ClientIdInitialValue() !== this.srxOAuth2CredentialsForm.value.signatureRxOAuth2ClientId;
        if (this.srxOAuth2CredentialsForm.value.signatureRxOAuth2ClientSecret || oAuth2ClientIdHasChanged) {
            await this.saveOAuth2Credentials();
            if (this.oAuth2CredentialsStatusResponse() === SrxCredentialsStatus.SrxOAuth2SaveFailed) {
                this.savingOrganization = false;
                return false;
            }
        }

        return true;
    }

    async saveLogoImage(organizationId: string) {
        if (!this.brandingLogoImageFormData && this.logoImageId === this.originalLogoImageId) return;

        await firstValueFrom(this.organizationService.uploadLogoImage(organizationId, this.brandingLogoImageFormData).pipe(
            filter(event => event.type === HttpEventType.Response),
            map((event: any) => event.body as FileDetails),
            tap((resFile: FileDetails) => {
                if (resFile) {
                    this.organizationForm.patchValue({logoImageId: resFile.id});
                }
                this.messageService.add(Message.Success("Logo Image updated."));
            }),
            catchError(errorResponse => {
                this.messageService.add(Message.Error(errorResponse.error, "Logo Image Uploading"));
                return of(null);
            })
        ));
    }

    onLogoImageUploadingError(event) {
        let errorDescripton = "The following files were not be uploaded: <br/>";
        if (event.files.length > 0) {
            for (const file of event.files) {
                errorDescripton += file.name + "<br/>";
            }
        }
        this.messageService.add(
            Message.Error(
                "An error occurred while uploading files. Please try again.<br/>" + errorDescripton,
                "Logo Image Uploading"
            )
        );
    }

    onGoverningBodyChange(value: string) {
        this.organizationForm.controls.name.updateValueAndValidity({onlySelf: true});
        if (value == null) {
            this.organizationForm.controls.sportTypeName.setValue(null);
            this.organizationForm.controls.duplicatePatientCheckType.setValue(DuplicatePatientCheckType.None);
            this.organizationForm.controls.monthsUntilEpisodeInactive.setValue(2);
            return;
        }
        const currentGovBody = this.allGovBodies.find(x => x.id == value);
        this.organizationForm.controls.sportTypeName.setValue(currentGovBody.sportTypeName);

        this._parentDuplicatePatientCheckType = currentGovBody.duplicatePatientCheckType;
        this.overrideDuplicatePatientCheckType = false;
        this.organizationForm.controls.duplicatePatientCheckType.setValue(this._parentDuplicatePatientCheckType);

        this.organizationForm.controls.systemCodeType.setValue(currentGovBody.systemCodeType);
        this._parentSystemCodeType = currentGovBody.systemCodeType;
        this.parentCanOrganisationsOverrideSystemCodeType = currentGovBody.canOrganisationsOverrideSystemCodeType;
        if (!this.parentCanOrganisationsOverrideSystemCodeType) {
            this.organizationForm.controls.overrideSystemCodeType.setValue(false);
        }

        this._parentMonthsUntilEpisodeInactive = currentGovBody.monthsUntilEpisodeInactive;
        this.overrideMonthsUntilEpisodeInactive = false;
        this.organizationForm.controls.monthsUntilEpisodeInactive.setValue(this._parentMonthsUntilEpisodeInactive);

        this.overridePassportDataSharing = false;
        this._parentPassportDataSharing = currentGovBody.passportDataSharing;
        this.organizationForm.controls.passportDataSharing.setValue(this._parentPassportDataSharing);

        this._parentAutoApproveSelfRegisteredPatients = currentGovBody.autoApproveSelfRegisteredPatients;
        this.overrideAutoApproveSelfRegisteredPatients = false
        this.organizationForm.controls.autoApproveSelfRegisteredPatients.setValue(this._parentAutoApproveSelfRegisteredPatients);

        this.parentMandatoryTwoFactorAuth = currentGovBody.mandatoryTwoFactorAuth;
        this.overrideMandatoryIdentityVerification = false;
        this.organizationForm.controls.mandatoryIdentityVerification.setValue(this.parentMandatoryTwoFactorAuth);

        this._parentEnablePatientPsdConfirmation = currentGovBody.enablePatientPsdConfirmation;
        this.overrideEnablePatientPsdConfirmation = false;
        this.organizationForm.controls.enablePatientPsdConfirmation.setValue(this._parentEnablePatientPsdConfirmation);

        if (!this.isCreate && this.organizationForm.get("sendNHSAccessStatusAlerts")?.value) {
            const selectedUserId = this.organizationForm.get("nhsAccessStatusAlertUserId")?.value;

            if (selectedUserId) {
              this.organizationService.updateNHSAccessStatusUser(selectedUserId, this.organizationId);
            }
        }
    }

    public async onTestSrxApiPressed() {
        this.testingSrxCredentials.set(true);
        try {
            const success = await this.srxService.testSrxCredentials(
                this.srxCredentialsForm.controls.signatureRxApiEmail.value,
                this.srxCredentialsForm.controls.signatureRxApiPassword.value,
                this.organizationId
            );
            this.srxCredentialsStatusResponse.set(
                success ? SrxCredentialsStatus.SrxCredentialsTestSuccess : SrxCredentialsStatus.SrxCredentialsTestFailed
            );
        } catch (error) {
            this.srxCredentialsStatusResponse.set(SrxCredentialsStatus.SrxCredentialsTestFailed);
        } finally {
            this.testingSrxCredentials.set(false);
        }
    }

    public async saveSrxApiCredentials() {
        this.savingSrxCredentials.set(true);
        try {
            const saveSuccess = await this.srxService.saveSrxCredentials(
                this.srxCredentialsForm.controls.signatureRxApiEmail.value,
                this.srxCredentialsForm.controls.signatureRxApiPassword.value,
                this.organizationId
            );

            this.srxCredentialsStatusResponse.set(
                saveSuccess ? SrxCredentialsStatus.SrxCredentialsSaveSuccess : SrxCredentialsStatus.SrxCredentialsSaveFailed
            );
        } catch (error) {
            this.srxCredentialsStatusResponse.set(SrxCredentialsStatus.SrxCredentialsSaveFailed);
        } finally {
            this.savingSrxCredentials.set(false);
        }
    }

    public async onTestOAuth2CredentialsPressed() {
        this.testingOAuth2Credentials.set(true);
        try {
            const success = await this.srxService.testOAuth2Credentials(
                this.srxOAuth2CredentialsForm.controls.signatureRxOAuth2ClientId.value,
                this.srxOAuth2CredentialsForm.controls.signatureRxOAuth2ClientSecret.value,
                this.organizationId
            );
            this.oAuth2CredentialsStatusResponse.set(
                success ? SrxCredentialsStatus.SrxOAuth2TestSuccess : SrxCredentialsStatus.SrxOAuth2TestFailed
            );
        } catch (error) {
            this.oAuth2CredentialsStatusResponse.set(SrxCredentialsStatus.SrxOAuth2TestFailed);
        } finally {
            this.testingOAuth2Credentials.set(false);
        }
    }

    public async saveOAuth2Credentials() {
        this.savingOAuth2Credentials.set(true);
        try {
            const saveSuccess = await this.srxService.saveOAuth2Credentials(
                this.srxOAuth2CredentialsForm.controls.signatureRxOAuth2ClientId.value,
                this.srxOAuth2CredentialsForm.controls.signatureRxOAuth2ClientSecret.value,
                this.organizationId
            );

            this.oAuth2CredentialsStatusResponse.set(
                saveSuccess ? SrxCredentialsStatus.SrxOAuth2SaveSuccess : SrxCredentialsStatus.SrxOAuth2SaveFailed
            );
        } catch (error) {
            this.oAuth2CredentialsStatusResponse.set(SrxCredentialsStatus.SrxOAuth2SaveFailed);
        } finally {
            this.savingOAuth2Credentials.set(false);
        }
    }

    onModuleEnabledChange(enabledModule: EnabledModule, isInitialRun: boolean = false) {
        if (enabledModule.moduleName == MiProModules.Consultations) {
            this.showEnablePatientDocumentUploadField = enabledModule.enabled;
        } else if (enabledModule.moduleName == MiProModules.Diary) {
            this.showSelfRegistrationURLField = enabledModule.enabled;
            this.showAppointmentBookingURLField = enabledModule.enabled;

            if(!isInitialRun) {
                this.organizationForm.patchValue({ diaryModuleVisibleLHSAndCard: enabledModule.enabled });
            }

            const eventsControl = this.enabledModules.controls.find(em => em.value.moduleName === MiProModules.EventsModule);
            if (enabledModule.enabled) {
                eventsControl.enable();
            } else {
                eventsControl.setValue({...eventsControl.value, enabled: false});
                eventsControl.disable();
            }
        } else if (enabledModule.moduleName === MiProModules.Prescriptions) {

            if (enabledModule.enabled) {
                this.preferredPharmacy.enable()
            } else {
                this.preferredPharmacy.disable()
                this.organizationForm.controls.signatureRxEnabled.setValue(false);
            }

        } else if (enabledModule.moduleName == MiProModules.Forms) {
            this.showFormioModules = enabledModule.enabled;
        } else if (enabledModule.moduleName == MiProModules.Reporting) {
            this.showSystemDashboards = enabledModule.enabled;
        }

        if (enabledModule.moduleName == MiProModules.NHS) {
            if (enabledModule.enabled) {
                if (!isInitialRun) {
                    this.organizationForm.patchValue({
                        selfRegisterPatientUrlLoginMode: SelfRegisterLoginMode.MiProAndNhs
                    });
                }

                this.showNHSAlertsSettings = true;

                if (this.originalCQCRegistrationNumber) {
                    this.cqcRegistrationNumber.reset(this.originalCQCRegistrationNumber);
                } else {
                    this.cqcRegistrationNumberDialogVisible = true;
                }
            } else {
                this.showNHSAlertsSettings = false;

                if (this.cqcRegistrationNumber.value) {
                    this.cqcRegistrationNumber.reset(null);
                }

                this.lockSelfRegLoginMode();
                this.organizationForm.patchValue({"mandatoryTwoFactorAuth": this.originalMandatoryTwoFactorAuth });
            }
            this.updateCQCRegNumberValidators(enabledModule);
        }
    }

    refreshAutoApproveTypeDropdown() {
        this.autoApproveTypeDropdown().disabled = !this.organizationForm.get("autoApproveSelfRegisteredPatients").value;
    }

    refreshSelfRegisterPatientUrlLoginMode() {
        if (!this.isNhsModuleEnabled) {
            this.lockSelfRegLoginMode();
        }
    }

    lockSelfRegLoginMode() {
        this.organizationForm.patchValue({
            selfRegisterPatientUrlLoginMode: SelfRegisterLoginMode.MiPro
        });
        this.selfRegLoginModeButtons()
            .filter(button => button.value !== SelfRegisterLoginMode.MiPro)
            .forEach(button => button.disabled = true);
    }

    onSelfRegPatientUrlEnabled(value: boolean) {
        if (value && this.isNhsModuleEnabled) {
            this.organizationForm.patchValue({
                selfRegisterPatientUrlLoginMode: SelfRegisterLoginMode.MiProAndNhs
            });
        }
    }

    logoImageUploading(event) {
        if (event.files !== undefined && event.files.length > 0) {
            const file = event.files[0];
            this.brandingLogoImageFormData = new FormData();
            this.brandingLogoImageFormData.append("file", file);
            this.brandingLogoImageFormData.append("last_modified", moment(file.lastModified).toDate().toUTCString());

            this.organizationForm.patchValue({ logoImageId : null, logoImageUrl: null });
            this.updatePreviewFromFormData(file);
        }

        this.logoImageUploader().clear();
    }

    updatePreviewFromFormData(file: File) {
        const reader = new FileReader();
        reader.onload = () => {
            this.brandingLogoImagePreviewSrc = reader.result as string;
        };
        reader.readAsDataURL(file);
    }

    clearLogoImage() {
        this.brandingLogoImageFormData = null;
        this.brandingLogoImagePreviewSrc = null;
        this.organizationForm.patchValue({ logoImageId : null, logoImageUrl: null });
    }

    async deleteModule(module: string) {
        let mod = this.allFormioModules.find(mod => mod.name === module);
        await this.formIOModuleService.delete(mod.id).then(() => location.reload());
    }

    async getSelfRegisterPatientURL() {
        if (!this.organizationForm.controls.name.value) {
            this.selfRegisterPatientURL = null;
        } else {
            const orgId: string = this.organizationForm.controls.id.value;
            const orgName: string = this.organizationForm.controls.name.value;
            this.organizationService.getSelfRegistrationUrl(orgId, orgName).then(response => {
                this.selfRegisterPatientURL = `${window.location.protocol}//${window.location.host}/selfregistration/clinic?clinic=${response.selfRegistrationURL}`;
            }).catch(_ => this.selfRegisterPatientURL = null);
        }
    }

    async getAppointmentBookingURL() {
        if (!this.organizationForm.controls.name.value) {
            this.appointmentBookingURL = null;
        } else {
            const orgId: string = this.organizationForm.controls.id.value;
            const orgName: string = this.organizationForm.controls.name.value;
            this.organizationService.getAppointmentBookingUrl(orgId, orgName).then(response => {
                this.appointmentBookingURL = `${window.location.protocol}//${window.location.host}/book-appointment?org=${response.appointmentBookingURL}`;
            }).catch(_ => this.appointmentBookingURL = null);
        }
    }

    private updatePreferredPharmacyValidators() {
        function isEmptyValue(value: any) {
            return value == null || ((typeof value === "string" || Array.isArray(value)) && value.length === 0);
        }

        const controls = this.preferredPharmacy.controls;

        this.preferredPharmacyRequiredFields = Object.keys(controls)
            .map(k => this.preferredPharmacy.get(k).value)
            .some(value => !isEmptyValue(value));

        const updateControls = [controls.name, controls.email, controls.addressLine1, controls.addressLine2, controls.postCode];

        updateControls.forEach(control => {
            this.preferredPharmacyRequiredFields
                ? control.addValidators(Validators.required)
                : control.removeValidators(Validators.required);
            control.updateValueAndValidity({emitEvent: false});
        });
    }

    protected clearPreferredPharmacyForm() {
        this.preferredPharmacy.reset();
    }

    private updateCQCRegNumberValidators(nhsModule: EnabledModule) {
        if (nhsModule?.enabled) {
            this.cqcRegistrationNumber.setValidators(Validators.required);
        } else {
            this.cqcRegistrationNumber.removeValidators(Validators.required);
        }
        this.cqcRegistrationNumber.updateValueAndValidity();
    }

    async loadUsersInOrganization(orgId: string) {
        const users = await this.organizationService.getUsersByOrganization(orgId);

        this.usersInOrganization = users.map(user => ({
            label: `${user.firstName ?? ""} ${user.lastName ?? ""} (${user.email ?? ""})`,
            value: user.userId
        }));

        const selectedId = this.organizationForm.get("nhsAccessStatusAlertUserId")?.value;

        if (!selectedId) {
            const orgAdmin = users.find(user =>
                user.roles?.includes(CoreRole.OrganisationAdmin)
            );

            if (orgAdmin) {
                this.organizationForm.patchValue({
                    nhsAccessStatusAlertUserId: orgAdmin.userId
                });
            }
        } else {
            this.organizationForm.patchValue({
                sendNHSAccessStatusAlerts: true
            });
        }
    }
}